import { createFileRoute, Link } from '@tanstack/react-router'
import { useState, useEffect, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { forgotPasswordApi } from '@/lib/api'
import { BRAND_MINT } from '@/lib/utils'
import { toast } from 'sonner'
import { ArrowLeftIcon, ArrowRightIcon, CheckCircleIcon, MailIcon, ZapIcon } from 'lucide-react'

export const Route = createFileRoute('/forgot-password')({
  component: () => {
    const [email, setEmail] = useState('')
    const [loading, setLoading] = useState(false)
    const [submitted, setSubmitted] = useState(false)

    const successHeadingRef = useRef<HTMLHeadingElement>(null)

    useEffect(() => {
      if (submitted && successHeadingRef.current) {
        successHeadingRef.current.focus()
      }
    }, [submitted])

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault()
      if (!email) {
        toast.error('Please enter your email address')
        return
      }

      setLoading(true)
      try {
        await forgotPasswordApi(email)
        setSubmitted(true)
        toast.success('Reset link sent if the account exists')
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Something went wrong'
        toast.error(message)
      } finally {
        setLoading(false)
      }
    }

    return (
      <div className="relative flex min-h-svh flex-col bg-[#0B1120] overflow-hidden">
        {/* Subtle grid pattern overlay */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        />

        {/* Decorative glow orbs */}
        <div className="pointer-events-none absolute -top-40 -right-40 size-[500px] rounded-full bg-[#00E5A0]/10 blur-[120px]" />
        <div className="pointer-events-none absolute -bottom-40 -left-40 size-[400px] rounded-full bg-[#00E5A0]/5 blur-[100px]" />
        <div className="pointer-events-none absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 size-[300px] rounded-full bg-[#00E5A0]/5 blur-[80px]" />

        {/* Brand lockup */}
        <div className="relative z-10 flex items-center justify-center pt-12 sm:pt-16">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="flex size-9 items-center justify-center rounded-xl bg-[#00E5A0] text-[#0B1120] text-sm font-bold shadow-lg shadow-[#00E5A0]/30">
              PP
            </div>
            <span className="text-lg font-semibold tracking-tight text-white">PeerPass</span>
          </Link>
        </div>

        {/* Forgot password form */}
        <div className="relative z-10 flex flex-1 items-center justify-center px-6 pb-12 sm:pb-16">
          <div className="w-full max-w-md transition-all duration-700 ease-out will-change-transform translate-y-0 opacity-100">
            <div className="relative rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl p-8 sm:p-10 shadow-2xl shadow-black/50">
              {/* Mint accent bar */}
              <span
                aria-hidden="true"
                className="absolute top-0 left-8 right-8 h-[2px] rounded-full"
                style={{ backgroundColor: BRAND_MINT }}
              />

              {submitted ? (
                /* Success state */
                <div className="flex flex-col items-center text-center">
                  <div className="flex size-14 items-center justify-center rounded-full bg-[#00E5A0]/15">
                    <MailIcon className="size-7 text-[#00E5A0]" />
                  </div>
                  <h1
                    ref={successHeadingRef}
                    tabIndex={-1}
                    className="mt-5 font-['Source_Serif_4',ui-serif,Georgia,serif] text-2xl font-medium tracking-tight text-white outline-none"
                  >
                    Check your inbox
                  </h1>
                  <p className="mt-2 text-sm text-white/60 leading-relaxed max-w-xs">
                    If an account with <span className="text-white/80 font-medium">{email}</span>{' '}
                    exists, we've sent a password reset link. It expires in 1 hour.
                  </p>
                  <div className="mt-6 rounded-xl border border-[#00E5A0]/15 bg-[#00E5A0]/[0.04] p-3 text-left w-full">
                    <div className="flex items-start gap-2.5">
                      <ZapIcon className="size-4 text-[#00E5A0] mt-0.5 shrink-0" />
                      <p className="text-xs text-white/50 leading-relaxed">
                        Didn't receive the email? Check your spam folder, or{' '}
                        <button
                          type="button"
                          onClick={() => setSubmitted(false)}
                          className="text-[#00E5A0] hover:text-[#00E5A0]/80 underline underline-offset-4 transition-colors"
                        >
                          try again
                        </button>
                        .
                      </p>
                    </div>
                  </div>
                  <Link
                    to="/login"
                    className="mt-6 inline-flex items-center gap-1.5 text-sm text-[#00E5A0] font-medium hover:text-[#00E5A0]/80 transition-colors"
                  >
                    <ArrowLeftIcon className="size-4" />
                    Back to log in
                  </Link>
                </div>
              ) : (
                /* Email form */
                <>
                  <div className="flex flex-col gap-2 text-center mb-8">
                    <h1 className="font-['Source_Serif_4',ui-serif,Georgia,serif] text-3xl font-medium tracking-tight text-white">
                      Reset your password
                    </h1>
                    <p className="text-sm text-white/60">
                      Enter your email and we'll send you a reset link.
                    </p>
                  </div>

                  <form onSubmit={handleSubmit} className="flex flex-col gap-5" aria-label="Reset password">
                    <div className="flex flex-col gap-2">
                      <Label htmlFor="reset-email" className="text-sm font-medium text-white/70">
                        Email address
                      </Label>
                      <div className="relative">
                        <MailIcon className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-white/30 pointer-events-none" />
                        <Input
                          id="reset-email"
                          type="email"
                          placeholder="you@example.com"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          autoComplete="email"
                          autoFocus
                          required
                          className="border-white/15 bg-white/[0.04] text-white placeholder:text-white/30 focus-visible:border-[#00E5A0] focus-visible:ring-[#00E5A0]/30 h-10 pl-10"
                        />
                      </div>
                    </div>

                    <Button
                      type="submit"
                      className="w-full bg-[#00E5A0] text-[#0B1120] hover:bg-[#00E5A0]/90 font-semibold text-base py-6 mt-2 shadow-xl shadow-[#00E5A0]/25 disabled:opacity-60"
                      disabled={loading}
                    >
                      {loading ? (
                        <span className="flex items-center gap-2">
                          <span className="size-4 rounded-full border-2 border-[#0B1120]/30 border-t-[#0B1120] animate-spin" />
                          Sending…
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          Send reset link
                          <ArrowRightIcon className="size-4" />
                        </span>
                      )}
                    </Button>
                  </form>

                  <div className="mt-6 text-center flex flex-col items-center gap-3">
                    <p className="text-sm text-white/40">
                      Remember your password?{' '}
                      <Link
                        to="/login"
                        className="text-[#00E5A0] font-medium hover:text-[#00E5A0]/80 underline underline-offset-4 transition-colors"
                      >
                        Log in
                      </Link>
                    </p>
                    <Link
                      to="/signup"
                      className="text-xs text-white/30 hover:text-white/50 transition-colors underline underline-offset-4"
                    >
                      Create an account
                    </Link>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Bottom trust bar */}
        <div className="relative z-10 flex justify-center pb-8">
          <div className="flex items-center gap-4 text-xs text-white/30">
            <span className="flex items-center gap-1.5">
              <CheckCircleIcon className="size-3 text-[#00E5A0]/60" />
              Employer-paid benefit
            </span>
            <span className="flex items-center gap-1.5">
              <CheckCircleIcon className="size-3 text-[#00E5A0]/60" />
              0% interest
            </span>
            <span className="flex items-center gap-1.5">
              <CheckCircleIcon className="size-3 text-[#00E5A0]/60" />
              100% anonymous
            </span>
          </div>
        </div>
      </div>
    )
  },
})
